import { Icon, IconName } from '@/components/Icon';
import colors, { appTheme } from '@/config/colors';
import { useNavigation } from '@react-navigation/native';
import {
  ReactElement,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  Platform,
  Pressable,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import { Text } from '@/components/Text';
import { ControlGroup, ExerciseControls } from './ExerciseControls';
import { isPatientApp } from '@/config/constants';
import { IS_WEB } from '@/utils/platform';

interface ExerciseNavigationHeaderProps {
  skipTitle: string;
  isSelf: boolean;
  exerciseRatingButtons?: ReactElement[];
  exerciseHelpButtons?: ReactElement[];
  exerciseClass: {
    iconName?: string;
    exerciseSettingsKey?: string;
    label?: string;
  };
  onPressHome: () => void;
  onPressSkip: () => void;
  activeExerciseClass: 'word' | 'sentence' | 'text';
}

interface HeaderTitleProps {
  isSelf: boolean;
  icon: IconName;
  selfTrainingTitle: string;
  title: string;
}

const styles = StyleSheet.create({
  isUpsideDown: {
    transform: [{ scaleY: -1 }, { scaleX: -1 }],
  },
});

function HeaderTitle({
  isSelf = false,
  icon,
  selfTrainingTitle,
  title,
}: HeaderTitleProps) {
  const color = !isPatientApp
    ? colors.white
    : isSelf
      ? colors.white
      : colors.darkGrey;

  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
      <Icon name={icon} size={isPatientApp ? 32 : 24} color={color} />
      <Text
        style={[
          {
            fontSize: isPatientApp ? 24 : 18,
            fontWeight: '600',
            color: color,
          },
        ]}
      >
        {isSelf ? selfTrainingTitle : title}
      </Text>
    </View>
  );
}

export function ExerciseNavigationHeader(props: ExerciseNavigationHeaderProps) {
  const screenSizeLimit = 1023;

  const {
    isSelf = false,
    exerciseClass,
    exerciseRatingButtons,
    exerciseHelpButtons,
    activeExerciseClass,
  } = props;
  const navigation = useNavigation();
  const headerTitleRef = useRef<View | null>(null);
  const actionsButtonRef = useRef<View | null>(null);

  const [isOverlapping, setIsOverlapping] = useState(false);

  const { width } = useWindowDimensions();

  useEffect(() => {
    if (width <= screenSizeLimit) {
      setIsOverlapping(true);
    }
  }, []);

  useLayoutEffect(() => {
    const tintColor = !isPatientApp
      ? colors.white
      : isSelf
        ? colors.white
        : colors.black;
    navigation.setOptions({
      headerStyle: {
        ...(Platform.OS !== 'web' && !isSelf && styles.isUpsideDown),
        height: 64,
        backgroundColor: isSelf ? appTheme.primary : colors.grey,
      },
      headerTitleStyle: {
        fontSize: 28,
        fontWeight: '600',
        color: tintColor,
      },
      headerLeftContainerStyle: {
        maxWidth: isSelf || activeExerciseClass === 'text' ? null : '5%',
        paddingLeft: 16,
      },
      headerTitle: () =>
        isSelf && (
          <View ref={headerTitleRef}>
            <HeaderTitle
              icon={exerciseClass.iconName as IconName}
              title={exerciseClass.label ?? ''}
              selfTrainingTitle={exerciseClass.exerciseSettingsKey ?? ''}
              isSelf={isSelf}
            />
          </View>
        ),
      headerLeft: () => (
        <>
          <Pressable
            onPress={props.onPressHome}
            testID="home-button"
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <Icon name="home" size={36} color={tintColor} />
          </Pressable>
          {!isSelf ? (
            <View ref={headerTitleRef} style={{ marginLeft: 12 }}>
              <HeaderTitle
                icon={exerciseClass.iconName as IconName}
                title={!isOverlapping ? (exerciseClass.label ?? '') : ''}
                selfTrainingTitle={exerciseClass.exerciseSettingsKey ?? ''}
                isSelf={isSelf}
              />
            </View>
          ) : null}
        </>
      ),
      headerUpsideDown: !IS_WEB && !isSelf ? true : false,
      headerRight: () => (
        <View
          className={`flex-row w-full space-x-2 ${isPatientApp && 'justify-end'}`}
        >
          {!isSelf && (
            <View ref={actionsButtonRef}>
              <ExerciseControls>
                <ControlGroup>{exerciseHelpButtons}</ControlGroup>
                {(exerciseRatingButtons ?? []).length > 0 && (
                  <ControlGroup>{exerciseRatingButtons}</ControlGroup>
                )}
              </ExerciseControls>
            </View>
          )}
          <Pressable
            onPress={props.onPressSkip}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 2,
              width: 150,
              justifyContent: 'flex-end',
            }}
          >
            <Text
              style={{
                fontSize: 18,
                top: -1,
                color: tintColor,
                fontWeight: '600',
              }}
            >
              {props.skipTitle}
            </Text>
            <Icon name="arrowRight" size={36} color={tintColor} />
          </Pressable>
        </View>
      ),
    });
  });

  return null;
}
